/**
 * DescriptionEditModal Component
 * Modal for editing playlist name, description, and privacy
 *
 * Features:
 * - Edit playlist name (required)
 * - Edit playlist description (optional, max 300 characters)
 * - Toggle playlist privacy (public/private)
 * - Character counter for description
 * - Real-time validation
 * - Integration with useUpdatePlaylist hook
 * - Loading state during save
 * - Empty description handled as undefined
 */

import { useState, useEffect } from 'react';
import { Modal, ModalActions } from '../shared/Modal';
import { Button } from '../shared/Button';
import { useUpdatePlaylist } from '../../api/hooks/usePlaylists';
import type { PlaylistDetail } from '../../api/types';

export interface DescriptionEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  playlist: PlaylistDetail;
  onSuccess?: (updated: { name: string; description: string | undefined; public: boolean }) => void;
}

const MAX_DESCRIPTION_LENGTH = 300;

export function DescriptionEditModal({
  isOpen,
  onClose,
  playlist,
  onSuccess,
}: DescriptionEditModalProps) {
  // State
  const [name, setName] = useState(playlist.name);
  const [description, setDescription] = useState(playlist.description || '');
  const [isPublic, setIsPublic] = useState(playlist.public);
  const [isSaving, setIsSaving] = useState(false);

  // Hooks
  const updatePlaylist = useUpdatePlaylist();

  // Reset form when playlist changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setName(playlist.name);
      setDescription(playlist.description || '');
      setIsPublic(playlist.public);
    }
  }, [isOpen, playlist.id, playlist.name, playlist.description, playlist.public]);

  // Validation
  const isNameEmpty = name.trim().length === 0;
  const isDescriptionTooLong = description.length > MAX_DESCRIPTION_LENGTH;
  const canSave = !isNameEmpty && !isDescriptionTooLong;

  /**
   * Handle save action
   * Updates playlist metadata and closes modal on success
   */
  const handleSave = async () => {
    if (!canSave) return;

    setIsSaving(true);
    try {
      // Convert empty description to undefined (not empty string or null)
      const descriptionValue = description.trim() || undefined;

      // Build update payload with ONLY changed fields
      // NOTE: 'public' field excluded due to Spotify API restriction
      const data: { name?: string; description?: string } = {};

      if (name.trim() !== playlist.name) {
        data.name = name.trim();
      }

      if (descriptionValue !== playlist.description) {
        data.description = descriptionValue;
      }

      await updatePlaylist.mutateAsync({
        playlistId: playlist.id,
        data,
      });

      // Notify parent component of successful update
      // NOTE: public field not updated due to Spotify API limitation
      onSuccess?.({
        name: name.trim(),
        description: descriptionValue,
        public: playlist.public, // Keep original value
      });

      // Close modal
      onClose();
    } catch (error) {
      console.error('Failed to update playlist:', error);
      // Error state is handled by the mutation hook
      // In production, this would trigger a toast notification
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle description change with length validation
   */
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    // Allow typing but show validation error
    setDescription(value);
  };

  return (
    <Modal open={isOpen} onClose={onClose} title="Edit Playlist Details" className="w-full max-w-lg">
      {/* Form Content */}
      <div className="p-6 space-y-4">
        {/* Name Field */}
        <div className="space-y-2">
          <label htmlFor="playlist-name" className="block text-sm font-medium text-zinc-300">
            Name <span className="text-red-400">*</span>
          </label>
          <input
            id="playlist-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`
              w-full px-3 py-2 bg-zinc-800 border rounded-lg text-zinc-200
              placeholder-zinc-500 focus:outline-none focus:ring-2
              transition-colors
              ${
                isNameEmpty
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-zinc-700 focus:ring-green-500'
              }
            `}
            placeholder="Enter playlist name"
            maxLength={100}
            autoFocus
          />
          {isNameEmpty && (
            <p className="text-xs text-red-400">Name is required</p>
          )}
        </div>

        {/* Description Field */}
        <div className="space-y-2">
          <label htmlFor="playlist-description" className="block text-sm font-medium text-zinc-300">
            Description
          </label>
          <textarea
            id="playlist-description"
            value={description}
            onChange={handleDescriptionChange}
            rows={4}
            className={`
              w-full px-3 py-2 bg-zinc-800 border rounded-lg text-zinc-200
              placeholder-zinc-500 focus:outline-none focus:ring-2
              resize-none transition-colors
              ${
                isDescriptionTooLong
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-zinc-700 focus:ring-green-500'
              }
            `}
            placeholder="Add a description (optional)"
          />
          {/* Character Counter */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-zinc-500">
              Optional description for your playlist
            </p>
            <span
              className={`text-xs ${
                isDescriptionTooLong ? 'text-red-400 font-medium' : 'text-zinc-500'
              }`}
            >
              {description.length}/{MAX_DESCRIPTION_LENGTH}
            </span>
          </div>
        </div>

        {/* Public/Private Toggle - DISABLED due to Spotify API restriction */}
        <div className="space-y-2">
          <label htmlFor="playlist-public" className="block text-sm font-medium text-zinc-300">
            Privacy
          </label>
          <div
            className="flex items-center justify-between p-3 bg-zinc-800 border border-zinc-700 rounded-lg opacity-60 cursor-not-allowed"
            title="Spotify API restriction: Unable to change playlist privacy via API. Use Spotify app to change this setting."
          >
            <div className="flex-1">
              <p className="text-sm text-zinc-200 font-medium">
                {isPublic ? 'Public' : 'Private'}
              </p>
              <p className="text-xs text-zinc-500 mt-0.5">
                {isPublic
                  ? 'Anyone can see and listen to this playlist'
                  : 'Only you can see this playlist'}
              </p>
              <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                API restriction - Use Spotify app to change
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isPublic}
              disabled
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full
                transition-colors cursor-not-allowed opacity-50
                ${isPublic ? 'bg-green-600' : 'bg-zinc-600'}
              `}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                  ${isPublic ? 'translate-x-6' : 'translate-x-1'}
                `}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <ModalActions>
        <Button variant="ghost" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!canSave || isSaving}
          isLoading={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
